import { useContext, useState } from 'react';
import { BookingContext } from '@sirkka-health/booking-system-ui';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	Box,
	Button,
	Card,
	CardContent,
	Grid,
	Step,
	StepLabel,
	Stepper,
	Typography,
} from '@mui/material';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import PersonalDetailsForm from './PersonalDetailsForm';
import Info from './ServiceInfoSection/ServiceInfo';
import InfoMobile from './ServiceInfoSection/ServiceInfoMobile';
import Review from './ReviewSelection';
import NurseSelection from './NurseMapSelection/index';
import { Footer, Header } from '@/components';
import { calculateHomeVisitPrice } from '@/utils';

const steps = ['Patient Details', 'Availability Selection', 'Review your booking'];

const getStepContent = (step, formData, setFormData) => {
	switch (step) {
		case 0:
			return <PersonalDetailsForm formData={formData} setFormData={setFormData} />;
		case 1:
			return <NurseSelection formData={formData} setFormData={setFormData} />;
		case 2:
			return <Review formData={formData} />;
		default:
			throw new Error('Unknown step');
	}
};

export default function HomeVisitBooking() {
	const { setBookingData } = useContext(BookingContext);
	const location = useLocation();
	const navigate = useNavigate();
	const selectedServiceItem = location.state?.selectedServiceItem || {};
	const bookingEntityId = location.state?.bookingEntityId || null;
	const [activeStep, setActiveStep] = useState(0);
	const [formData, setFormData] = useState({
		firstName: '',
		lastName: '',
		gender: '',
		dateOfBirth: '',
		emailAddress: '',
		addressLine1: '',
		addressLine2: '',
		city: '',
		postcode: '',
		selectedService: selectedServiceItem,
		selectedNurseId: null,
		selectedNurseName: null,
		selectedNursePrice: null,
		selectedDate: '',
		selectedTime: '',
		bookingEntityId: bookingEntityId,
	});

	const requiredPersonalFields = [
		'firstName',
		'lastName',
		'gender',
		'dateOfBirth',
		'emailAddress',
		'addressLine1',
		'city',
		'postcode',
	];

	const requiredApptFields = ['selectedService', 'selectedNurseId', 'selectedDate', 'selectedTime'];

	const isFormValid = (requiredFields) =>
		requiredFields.every(
			(field) => formData[field]?.toString().trim() !== '' && formData[field] != null
		);

	const handleNext = () => {
		if (activeStep === 0 && isFormValid(requiredPersonalFields)) {
			setActiveStep((prev) => prev + 1);
		} else if (activeStep === 1 && isFormValid(requiredApptFields)) {
			setActiveStep((prev) => prev + 1);
		} else if (activeStep === 2) {
			handleSubmit();
		}
	};

	const handleBack = () => setActiveStep((prev) => prev - 1);

	const handleSubmit = () => {
		const appointmentLocation = [
			formData.addressLine1,
			formData.addressLine2,
			formData.city,
			formData.postcode,
			'United Kingdom',
		]
			.filter((part) => part && part.trim() !== '')
			.join(', ');

		const newBooking = {
			booking: {
				bookingId: Date.now().toString(),
				locationId: 4,
				bookingName: `${formData.firstName} ${formData.lastName}`,
				bookingProduct: selectedServiceItem.service_name,
				appointmentLocation,
				appointmentRegion: 'Home Visit',
				appointmentDate: formData.selectedDate,
				appointmentTime: formData.selectedTime,
				appointmentNotes: '',
				appointmentFor: 'myself',
				sampleConsent: true,
				termsconditions: true,
				completed: true,
			},
			customer: {
				nameTitle: '',
				firstName: formData.firstName,
				lastName: formData.lastName,
				emailAddress: formData.emailAddress || 'Not provided',
				patientEmailAddress: formData.emailAddress || 'Not provided',
				gender: formData.gender,
				dateOfBirth: formData.dateOfBirth,
				phoneNumber: formData.phoneNumber,
				company: '',
				occupation: '',
				address_line_1: formData.addressLine1,
				address_line_2: formData.addressLine2 || '',
				city: formData.city,
				postcode: formData.postcode,
				country: 'United Kingdom',
			},
			service: {
				serviceName: selectedServiceItem.service_name,
				serviceId: selectedServiceItem.service_code,
				serviceType: selectedServiceItem.service_type,
				emailTemplate: selectedServiceItem.email_template,
				regionId: 5,
				price: calculateHomeVisitPrice(
					selectedServiceItem.service_price,
					formData?.selectedNursePrice
				),
				apptStackable: selectedServiceItem.appt_stackable,
				apptDuration: selectedServiceItem.appt_duration,
			},
			entity: {
				entityId: bookingEntityId,
			},
			clinician: {
				clinicianId: formData.selectedNurseId,
				clinicianName: formData.selectedNurseName,
				firstPersonRate: formData.selectedNursePrice,
			},
			combineBooking: '',
		};

		setBookingData((prev) => [...prev, newBooking]);
		navigate('/cart');
	};

	return (
		<>
			<Header />
			<Grid
				container
				sx={{
					display: 'flex',
					flexWrap: 'nowrap',
					height: 'auto',
					mt: { xs: 4, sm: 0 },
				}}
			>
				<Grid
					xs={12}
					sm={5}
					md={5}
					lg={5}
					sx={{
						display: { xs: 'none', md: 'flex' },
						flexDirection: 'column',
						backgroundColor: 'background.paper',
						borderRight: { sm: 'none', md: '1px solid' },
						borderColor: { sm: 'none', md: 'divider' },
						alignItems: 'start',
						pt: 16,
						px: 6,
						gap: 4,
					}}
				>
					<Box sx={{ flexGrow: 1, width: '100%', maxWidth: 500 }}>
						<Info serviceData={selectedServiceItem} />
					</Box>
				</Grid>

				{/* Right side form content */}
				<Grid
					xs={12}
					sm={7}
					md={7}
					lg={7}
					sx={{
						display: 'flex',
						flexDirection: 'column',
						maxWidth: '100%',
						backgroundColor: { xs: 'transparent', sm: 'background.default' },
						alignItems: 'start',
						py: { xs: 4, sm: 8 },
						px: { xs: 2, sm: 10 },
						gap: { xs: 4, md: 8 },
					}}
				>
					<Box sx={{ maxWidth: { sm: '100%', md: 600 }, flexGrow: 1 }}>
						<Stepper
							activeStep={activeStep}
							alternativeLabel
							sx={{
								display: { sm: 'flex', md: 'flex' },
								my: 4,
							}}
						>
							{steps.map((label) => (
								<Step key={label}>
									<StepLabel>{label}</StepLabel>
								</Step>
							))}
						</Stepper>

						<Card sx={{ display: { xs: 'flex', md: 'none' }, width: '100%' }}>
							<CardContent
								sx={{
									display: 'flex',
									width: '100%',
									alignItems: 'center',
									justifyContent: 'space-between',
								}}
							>
								<div>
									<Typography variant="subtitle2" gutterBottom>
										Service Details
									</Typography>
								</div>
								<InfoMobile serviceData={selectedServiceItem} />
							</CardContent>
						</Card>

						<>
							<Box sx={{ mb: 4 }}>{getStepContent(activeStep, formData, setFormData)}</Box>
							<Box
								sx={{
									display: 'flex',
									flexDirection: { xs: 'column-reverse', sm: 'row' },
									alignItems: 'end',
									flexGrow: 1,
									gap: 1,
									mt: { xs: 2, sm: 0 },
									mb: 4,
									justifyContent: activeStep !== 0 ? 'space-between' : 'flex-end',
								}}
							>
								{activeStep !== 0 && (
									<Button
										startIcon={<ChevronLeftRoundedIcon />}
										onClick={handleBack}
										variant="text"
										sx={{ display: { xs: '100%', sm: 'flex' } }}
									>
										Previous
									</Button>
								)}

								<Button
									variant="contained"
									endIcon={<ChevronRightRoundedIcon />}
									onClick={handleNext}
									disabled={
										(activeStep === 0 && !isFormValid(requiredPersonalFields)) ||
										(activeStep === 1 && !isFormValid(requiredApptFields))
									}
									sx={{ width: { xs: '100%', sm: 'fit-content' } }}
								>
									{activeStep === steps.length - 1 ? 'Proceed to booking' : 'Next'}
								</Button>
							</Box>
						</>
					</Box>
				</Grid>
			</Grid>
			<Footer />
		</>
	);
}
